'use client';

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import { PLANS } from '@/app/config/plans';
import { fetchPricingCatalog, fetchPricingConfig, plansForCatalog } from '@/app/utils/pricing/api';
import { reportPricingFailure } from '@/app/utils/pricing/reporting';
import type { PricingConfig, PricingMode } from '@/app/utils/pricing/types';

export interface PricingSnapshot {
  config: PricingConfig | null;
  mode: PricingMode;
  plans: typeof PLANS | null;
}
interface RuntimePricing extends PricingSnapshot {
  config: PricingConfig | null;
  mode: PricingMode;
  plans: typeof PLANS | null;
  loading: boolean;
  revision: number;
  refresh: () => Promise<PricingConfig | null>;
  enterCheckout: () => () => void;
}
const PricingContext = createContext<RuntimePricing | null>(null);

/** A successful, uncached server config is the only way to enter legacy mode. */
export function PricingProvider({
  children,
  initial,
  entry = true,
}: {
  children: ReactNode;
  initial?: PricingSnapshot;
  entry?: boolean;
}) {
  const [state, setState] = useState<Omit<RuntimePricing, 'refresh' | 'enterCheckout'>>({
    config: null,
    mode: 'paused',
    plans: null,
    loading: entry && !initial?.config,
    revision: 0,
    ...initial,
  });
  const request = useRef(0);
  const checkoutCount = useRef(0);
  const entered = useRef(false);
  const mounted = useRef(false);
  const refresh = useCallback(async () => {
    const sequence = ++request.current;
    // Disable submissions during refresh; invalidate consent when policy or offers change.
    setState((s) => ({ ...s, loading: !s.config }));
    try {
      const config = await fetchPricingConfig();
      const catalog =
        config.clients.penzu_next.mode === 'legacy' ? null : await fetchPricingCatalog();
      if (!mounted.current || sequence !== request.current) return null;
      if (catalog && catalog.pricing_version !== config.pricing_catalog_version)
        throw new Error('Catalogue changed');
      const plans = catalog ? plansForCatalog(catalog) : PLANS;
      setState((s) => ({
        config,
        mode: config.clients.penzu_next.mode,
        plans,
        loading: false,
        revision:
          s.revision +
          (JSON.stringify([s.config, s.plans]) === JSON.stringify([config, plans]) ? 0 : 1),
      }));
      return config;
    } catch (error) {
      if (mounted.current && sequence === request.current) {
        reportPricingFailure('catalogue_refresh', error);
        setState((s) => ({
          ...s,
          mode: 'paused',
          loading: false,
          revision: s.revision + 1,
        }));
      }
      return null;
    }
  }, []);
  useEffect(() => {
    mounted.current = true;
    if (entry && !initial?.config && !entered.current) {
      entered.current = true;
      void refresh();
    }
    const foreground = () => {
      if (checkoutCount.current > 0 && !document.hidden && navigator.onLine) void refresh();
    };
    const timer = window.setInterval(foreground, 30_000);
    document.addEventListener('visibilitychange', foreground);
    window.addEventListener('online', foreground);
    return () => {
      mounted.current = false;
      window.clearInterval(timer);
      document.removeEventListener('visibilitychange', foreground);
      window.removeEventListener('online', foreground);
    };
  }, [refresh, entry, initial]);
  const enterCheckout = useCallback(() => {
    checkoutCount.current += 1;
    entered.current = true;
    void refresh(); // Every modal/standalone entry refreshes, independently of prior public reads.
    return () => {
      checkoutCount.current = Math.max(0, checkoutCount.current - 1);
    };
  }, [refresh]);
  return (
    <PricingContext.Provider value={{ ...state, refresh, enterCheckout }}>
      {children}
    </PricingContext.Provider>
  );
}

export function usePricing(): RuntimePricing {
  const context = useContext(PricingContext);
  if (!context) throw new Error('usePricing must be used within PricingProvider');
  return context;
}

/** Optional telemetry context; its absence never authorizes a payment. */
export function usePricingSnapshot(): RuntimePricing | null {
  return useContext(PricingContext);
}

export function useCheckoutPricingActivity() {
  const { enterCheckout } = usePricing();
  useEffect(() => enterCheckout?.(), [enterCheckout]);
}

export function PricingUnavailable() {
  const { loading, refresh } = usePricing();
  return (
    <div role="status" className="my-4 text-sm text-slate-600">
      <p>
        {loading
          ? 'Loading current pricing…'
          : 'Purchases are temporarily unavailable. Please refresh or contact support.'}
      </p>
      {!loading && (
        <button type="button" className="underline mt-2" onClick={() => void refresh()}>
          Refresh pricing
        </button>
      )}
    </div>
  );
}
